import {Dictionary, DrupalProductVariationGroup, DrupalProductVariationProductNumber} from "@/types/hygiena-types";
import {Link} from "@/components/atoms/link";
import {Fragment, useContext} from "react";
import {DictionaryContext} from "@/context/dictionary-context";

/**
 * Renders product the number(s) from a variant object.
 *
 * @param {DrupalProductVariationGroup|DrupalProductVariationGroup[]} [variant] - A single or multiple variant objects.
 * @param {boolean} [showModal] - Whether to show the lyokit modal.
 * @param {boolean} [singleLine] - Whether to show multiple product numbers on a single line.
 * @param {boolean} [showSku] - Whether to show the sku.
 */
export default function RenderProductNumber({
  variant,
  showModal,
  singleLine,
  showSku,
}: {
  variant?: DrupalProductVariationGroup|DrupalProductVariationGroup[],
  showModal?: boolean,
  singleLine?: boolean,
  showSku?: boolean,
}) {
  const t = useContext<Dictionary>(DictionaryContext);
  const label = t?.all?.product_number;
  if (!variant || (Array.isArray(variant) && !variant.length)) return <></>;
  const variants: DrupalProductVariationGroup[] = Array.isArray(variant) ? variant : [variant];

  /**
   * Renders the product number details.
   *
   * @param {DrupalProductVariationProductNumber} item - The product variation number.
   */
  const render = (
    item: DrupalProductVariationProductNumber,
  ) => {
    let label = ``;
    let hasDetail = false;
    let hasQuantity = false;
    if (item?.detail) {
      label = `${item.detail}`;
      hasDetail = true;
    }
    if (hasDetail || hasQuantity) {
      label = `${label}: `;
    }
    label = `${label} ${item.number}`;

    if (showSku && item.sku && item.number !== item.sku) {
      label = `${label} (${item.sku})`;
    }

    return label;
  }

  if (singleLine) return (
    <div className="flex">
      <div className="w-full min-h-[50px]">
        <label className="mr-2 flex-grow">
          {label && `${label}: `}{
            variants.map(group => group.productNumbers.map(
              item => render(item)).join(', ')
            )
          }
        </label>
      </div>
    </div>
  );

  /**
   * Determines if the show modal button should be presented.
   */
  const showModalButton = (): boolean => {
    if (showModal) {
      if (variants?.length === 1 && variants?.[0]?.modal) {
        return true;
      } else if (variants?.length > 1) {
        return variants.filter(variant => variant?.modal)?.length > 0;
      }
    }
    return false;
  }

  return (
    <div className="flex">
      <div className="w-full min-h-[50px]">
        {label && (
          <label className="mr-2 flex-grow">{label}:</label>
        )}
        {variants?.length > 1 ? (
          <div className="flex flex-wrap flex-col gap-1 mt-1">
            {variants.map((variant, index) => (
              <Fragment key={`product-variation-number--${index}--${variant.productNumbers.map(item => item.number).join('-')}`}>
                {variant.productNumbers.map((item, key) => (
                  <div key={`active-product-number--${key}`}>
                    <span>{render(item)}</span>
                  </div>
                ))}
              </Fragment>
            ))}
          </div>
        ): (
          <>
            {variants?.[0]?.productNumbers?.length > 1 ? (
              <div className="flex flex-wrap flex-col gap-1 mt-1">
                {variants[0].productNumbers.map((item, key) => (
                  <div key={`active-product-number--${key}`}>
                    <span>{render(item)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <span>{render(variants[0].productNumbers[0])}</span>
            )}
          </>
        )}
      </div>
      {showModalButton() && (
        <div>
          <Link
            className="border border-primary text-primary py-0.5 px-2 font-bold cursor-pointer rounded-sm text-lg hover:border-complimentary hover:text-complimentary transition-all"
            // The following link is a Drupal Redirect, when the file is updated, the redirect will also need to be updated.
            href="/documents/85796/Cycler-Profile-Compatibility-Chart-for-LyoKits.pdf" target={"_blank"}>?</Link>
        </div>
      )}
    </div>
  );
}
